import { useParams, Link, Navigate } from "react-router-dom";
import { WalletShell, NAVY, NAVY_DARK, GOLD } from "@/components/wallet/WalletShell";
import { ArrowLeft, CheckCircle2, Circle, Clock, Building2, ShieldCheck,
         BadgeCheck, FileText, Activity, AlertTriangle, TrendingUp } from "lucide-react";

/**
 * WalletInvestmentScenario — single-scenario mockup, static data.
 *
 * Illustrates the full Trust journey for one investment product:
 * institution's Trust Request → Trust Workflow steps →
 * Evidence produced → Credentials issued → Outcome Governance.
 *
 * Consumers of this page: prospective enterprise customers walking
 * through a proof-of-value. Once M4 lands the data plumbs into real
 * TrustRequest / TrustCredential / Outcome objects.
 */

// ─── Scenario definitions ────────────────────────────────────
const SCENARIOS = {
    "unit-trust": {
        product: "Unit Trust",
        title: "Opening a discretionary Unit Trust",
        institution: "Coronation Investments",
        purpose: "Open the Coronation Balanced Fund at R1,000/mo debit-order.",
        steps: [
            { name: "Identity verification", primitive: "identity_verification", status: "completed", provider: "Smile ID · SA ID match", latency: 340, evidence: "Passport + SA ID biometric match" },
            { name: "FICA source-of-funds", primitive: "consent",                status: "completed", provider: "Payslip upload · consumer-supplied", latency: 220, evidence: "Payslip Feb 2026, salary R42k" },
            { name: "FAIS risk profile",     primitive: "assessment",            status: "completed", provider: "Altara Risk-Profile Adapter", latency: 180, evidence: "12-question FAIS Category I profile → Moderate-Aggressive" },
            { name: "Suitability check",     primitive: "evaluation",            status: "completed", provider: "Coronation Suitability Rules", latency: 90,  evidence: "Balanced Fund suits risk profile + 10y horizon" },
            { name: "Affordability check",   primitive: "affordability",         status: "completed", provider: "Nova Bank affordability API", latency: 410, evidence: "Debit-order affordable · disposable income R12k/mo" },
            { name: "Debit-order mandate",   primitive: "consent",                status: "completed", provider: "Consumer signed",             latency: 60,  evidence: "Ed25519-signed mandate · consumer wallet" },
        ],
        credentials_issued: [
            { name: "KYC · FICA Verified",             issuer: "Coronation Investments", category: "identity",     reusable: true },
            { name: "FAIS Risk Profile · Moderate-Aggressive", issuer: "Coronation Investments", category: "suitability", reusable: true },
            { name: "Affordability Certificate",       issuer: "Nova Bank",              category: "financial",    reusable: false },
        ],
        outcomes: [
            { type: "suitability_confirmed",  status: "good",   risk_level: "low",     confidence: 0.94, explanation: "Product matches risk profile, investment horizon, and disposable income." },
            { type: "fais_advice_evidenced",  status: "good",   risk_level: "low",     confidence: 0.98, explanation: "Complete Category I advice record captured as immutable evidence." },
        ],
        callout: {
            icon: TrendingUp,
            title: "Reusable across the industry",
            body: "The KYC + FAIS Risk Profile credentials become part of the consumer's Trust Wallet. Opening the next Unit Trust with a different manager takes ~90 seconds.",
        },
    },
    "tfsa": {
        product: "Tax-Free Savings Account",
        title: "TFSA onboarding + lifetime-limit governance",
        institution: "Satrix Investments",
        purpose: "Open a Satrix Top 40 TFSA at R1,500/mo — SARS annual limit R36k.",
        steps: [
            { name: "Identity verification", primitive: "identity_verification", status: "completed", provider: "Existing Passport credential", latency: 20,  evidence: "Reused Trust Credential — no re-KYC" },
            { name: "TFSA contribution check", primitive: "compliance",         status: "completed", provider: "Altara TFSA-Aggregator Adapter", latency: 380, evidence: "Existing YTD contributions R12k (Standard Bank). R24k headroom remaining." },
            { name: "Lifetime cap check",   primitive: "compliance",             status: "completed", provider: "Altara Lifetime-Cap Adapter",  latency: 120, evidence: "Lifetime contributions R58k of R500k cap." },
            { name: "Product suitability",   primitive: "evaluation",            status: "completed", provider: "Satrix Suitability Rules",       latency: 60,  evidence: "TFSA suits stated long-term saving goal." },
            { name: "Debit-order mandate",   primitive: "consent",               status: "completed", provider: "Consumer signed",              latency: 55,  evidence: "Ed25519-signed mandate" },
        ],
        credentials_issued: [
            { name: "TFSA Contribution Headroom · R24k available", issuer: "Altara TFSA Aggregator", category: "compliance", reusable: true },
            { name: "Suitability Certificate · TFSA",              issuer: "Satrix Investments",     category: "suitability", reusable: true },
        ],
        outcomes: [
            { type: "regulatory_limit_respected", status: "good",       risk_level: "low",     confidence: 1.00, explanation: "Combined annual TFSA contributions (R30k) stay under R36k cap." },
            { type: "cross_provider_visibility",  status: "acceptable", risk_level: "low",     confidence: 0.90, explanation: "SARS receives a single aggregated view via the Trust Wallet." },
        ],
        callout: {
            icon: ShieldCheck,
            title: "One TFSA credential, every provider",
            body: "SARS penalties (40% on over-contributions) become impossible because every TFSA provider sees the same aggregated headroom via Altara — before the debit-order runs.",
        },
    },
    "retirement-annuity": {
        product: "Retirement Annuity",
        title: "RA suitability + Regulation 28 monitoring",
        institution: "10X Investments",
        purpose: "Open a Regulation-28-compliant RA — R2,500/mo contribution.",
        steps: [
            { name: "Identity + FICA",      primitive: "identity_verification", status: "completed", provider: "Existing Trust Wallet",         latency: 15,  evidence: "Passport reused · no re-verification" },
            { name: "Section 10C limit",    primitive: "compliance",           status: "completed", provider: "SARS 10C adapter",                latency: 210, evidence: "R30k YTD contribution · 27.5% of R500k income = R137k cap. Room to contribute." },
            { name: "FAIS advice + record", primitive: "assessment",           status: "completed", provider: "10X Advice Engine",               latency: 610, evidence: "Category I advice · 25y horizon · glide-path portfolio recommended" },
            { name: "Regulation 28 check",   primitive: "compliance",           status: "completed", provider: "10X Reg-28 rules engine",         latency: 340, evidence: "75% equity / 30% offshore / 25% property — inside Reg 28 limits" },
            { name: "Debit-order mandate",   primitive: "consent",              status: "completed", provider: "Consumer signed",                latency: 55,  evidence: "Ed25519-signed mandate" },
        ],
        credentials_issued: [
            { name: "FAIS Advice Record · RA",           issuer: "10X Investments",   category: "advice",    reusable: false },
            { name: "Section 10C Contribution Room",     issuer: "SARS Adapter",       category: "tax",       reusable: true },
            { name: "Regulation 28 Compliance Snapshot", issuer: "10X Investments",   category: "compliance", reusable: false },
        ],
        outcomes: [
            { type: "advice_evidenced",       status: "good",      risk_level: "low",    confidence: 0.97, explanation: "Full FAIS-compliant advice record captured; retrievable by the consumer forever." },
            { type: "regulation_28_compliant", status: "good",      risk_level: "low",    confidence: 1.00, explanation: "Portfolio breakdown continuously monitored — alerts if drift takes it non-compliant." },
        ],
        callout: {
            icon: Activity,
            title: "Continuous, not annual",
            body: "Regulation 28 monitoring runs continuously. If a portfolio drifts out of compliance the Outcome Governance engine emits an `attention` outcome — before a regulator does.",
        },
    },
    "living-annuity": {
        product: "Living Annuity",
        title: "At-retirement conversion + annual drawdown review",
        institution: "Allan Gray",
        purpose: "Convert R4.2m RA into a Living Annuity · 6% initial drawdown.",
        steps: [
            { name: "Identity + FICA",      primitive: "identity_verification", status: "completed", provider: "Trust Wallet (reused)", latency: 12,  evidence: "Passport reused" },
            { name: "Drawdown suitability", primitive: "assessment",           status: "completed", provider: "Allan Gray Retirement Advice", latency: 720, evidence: "6% drawdown at age 65 sustainable to age 90 in 85% of Monte-Carlo paths" },
            { name: "SARS transfer approval", primitive: "compliance",         status: "completed", provider: "SARS Retirement Fund adapter", latency: 480, evidence: "Section 37C confirmed" },
            { name: "Investment strategy",   primitive: "assessment",          status: "completed", provider: "Allan Gray Balanced Fund",    latency: 90,  evidence: "50% Balanced Fund / 50% Stable Fund — inflation-plus-4% target" },
            { name: "Consumer decision + consent", primitive: "consent",       status: "completed", provider: "Consumer signed",             latency: 55,  evidence: "Ed25519-signed conversion consent" },
        ],
        credentials_issued: [
            { name: "Living Annuity · Suitability", issuer: "Allan Gray",         category: "suitability", reusable: false },
            { name: "Annual Drawdown Review · Due Feb 2027", issuer: "Allan Gray", category: "compliance", reusable: false },
        ],
        outcomes: [
            { type: "sustainability_at_risk", status: "attention",   risk_level: "medium",  confidence: 0.85, explanation: "6% drawdown is sustainable but flag consumer if a market drawdown of >20% occurs — annual review will re-assess." },
            { type: "advice_evidenced",       status: "good",        risk_level: "low",     confidence: 0.96, explanation: "Complete Category II advice record captured." },
        ],
        callout: {
            icon: AlertTriangle,
            title: "The annual review is an Outcome, not a form",
            body: "Every anniversary the Outcome Governance engine re-evaluates drawdown sustainability against the latest portfolio value. Advisors intervene when the outcome shifts from `good` to `attention` — well before consumer harm.",
        },
    },
};

const STATUS_TONE = {
    good:       { chip: "bg-emerald-50 text-emerald-700 border-emerald-200",  dot: "#10B981" },
    acceptable: { chip: "bg-sky-50 text-sky-700 border-sky-200",              dot: "#0284C7" },
    attention:  { chip: "bg-amber-50 text-amber-800 border-amber-200",        dot: "#D97706" },
    at_risk:    { chip: "bg-rose-50 text-rose-700 border-rose-200",           dot: "#E11D48" },
};


export default function WalletInvestmentScenario() {
    const { slug } = useParams();
    const scenario = SCENARIOS[slug];
    if (!scenario) return <Navigate to="/wallet/investments" replace />;

    return (
        <WalletShell activeTab="investments">
            <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-8" data-testid={`investment-scenario-page-${slug}`}>

                <Link to="/wallet/investments" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="scenario-back">
                    <ArrowLeft className="w-3.5 h-3.5" /> All scenarios
                </Link>

                {/* Hero */}
                <section
                    className="rounded-3xl overflow-hidden shadow-xl relative"
                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)` }}
                >
                    <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full" style={{ background: `${GOLD}18` }} />
                    <div className="relative px-6 lg:px-12 py-10 text-white">
                        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
                            {scenario.product}
                        </div>
                        <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-2 leading-tight">
                            {scenario.title}
                        </h1>
                        <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">
                            {scenario.purpose}
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 border border-white/20 rounded-full px-3 py-1.5">
                            <Building2 className="w-3.5 h-3.5" style={{ color: GOLD }} />
                            <span className="text-xs text-white/80">Requested by <span className="font-medium text-white">{scenario.institution}</span></span>
                        </div>
                    </div>
                </section>

                {/* Workflow steps */}
                <section data-testid="scenario-workflow">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-4">
                        The Trust Workflow
                    </div>
                    <div className="border border-slate-200 rounded-2xl bg-white divide-y divide-slate-100">
                        {scenario.steps.map((s, i) => (
                            <div key={s.name} className="p-4 flex items-start gap-4" data-testid={`workflow-step-${i}`}>
                                <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 bg-emerald-50 border border-emerald-200">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-slate-900">{s.name}</span>
                                        <span className="text-[10px] uppercase tracking-widest text-slate-400">{s.primitive}</span>
                                        <span className="text-[10px] font-mono text-slate-400 tabular-nums ml-auto">{s.latency}ms</span>
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">
                                        <span className="text-slate-700">Provider:</span> {s.provider}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">
                                        <span className="text-slate-700">Evidence:</span> {s.evidence}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Credentials issued */}
                <section data-testid="scenario-credentials">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-4">
                        Credentials issued to your Trust Wallet
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {scenario.credentials_issued.map(c => (
                            <div key={c.name} className="border border-slate-200 rounded-2xl bg-white p-4">
                                <div className="flex items-center gap-2">
                                    <BadgeCheck className="w-4 h-4 text-slate-600" />
                                    <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{c.category}</span>
                                    {c.reusable && (
                                        <span className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                                            Reusable
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 text-sm font-semibold text-slate-900 leading-snug">{c.name}</div>
                                <div className="mt-1 text-xs text-slate-500">Issued by {c.issuer}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Outcomes */}
                <section data-testid="scenario-outcomes">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-4">
                        Outcome Governance
                    </div>
                    <div className="space-y-3">
                        {scenario.outcomes.map(o => {
                            const tone = STATUS_TONE[o.status] || STATUS_TONE.acceptable;
                            return (
                                <div key={o.type} className="border border-slate-200 rounded-2xl bg-white p-4 flex items-start gap-4">
                                    <div
                                        className="w-2.5 h-2.5 rounded-full shrink-0 mt-2"
                                        style={{ background: tone.dot }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-900">{o.type.replace(/_/g, " ")}</span>
                                            <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${tone.chip}`}>
                                                {o.status}
                                            </span>
                                            <span className="text-[10px] font-mono text-slate-400 tabular-nums ml-auto">
                                                confidence {Math.round(o.confidence * 100)}%
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{o.explanation}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Callout */}
                <section
                    className="rounded-2xl p-6 relative overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)` }}
                >
                    <div className="relative flex items-start gap-4 text-white">
                        <div className="w-10 h-10 rounded-md border border-white/20 flex items-center justify-center shrink-0">
                            <scenario.callout.icon className="w-4 h-4" style={{ color: GOLD }} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                                Why it matters
                            </div>
                            <h3 className="mt-1 font-display text-lg font-semibold leading-snug">{scenario.callout.title}</h3>
                            <p className="mt-2 text-sm text-white/80 leading-relaxed">{scenario.callout.body}</p>
                        </div>
                    </div>
                </section>

            </div>
        </WalletShell>
    );
}
